import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { adminApi } from '../../utils/api';
import Button from './shared/Button';
import Badge from './shared/Badge';
import Card from './shared/Card';
import InfoRow from './shared/InfoRow';
import Tabs from './shared/Tabs';
import Input from './shared/Input';
import Textarea from './shared/Textarea';
import Spinner from './shared/Spinner';
import Alert from './shared/Alert';

const TAB_LIST = [
    { id: 'workbench', label: 'Query Workbench' },
    { id: 'indexes', label: 'Indexes' },
    { id: 'config', label: 'Config' },
];

function ConfigTab({ store }) {
    if (!store) return null;
    const config = store.config || {};
    return (
        <Card title="Connection Configuration" description="Sensitive values are masked">
            <InfoRow label="Provider" value={<Badge>{store.provider}</Badge>} />
            <InfoRow label="Status" value={
                <Badge variant={store.status === 'connected' ? 'success' : 'danger'} dot>
                    {store.status}
                </Badge>
            } />
            {config.url && <InfoRow label="URL" value={config.url} mono />}
            {config.bucket && <InfoRow label="Bucket" value={config.bucket} mono />}
            {config.region && <InfoRow label="Region" value={config.region} />}
            {config.api_key && <InfoRow label="API Key" value={config.api_key} mono />}
            {config.aws_access_key && <InfoRow label="AWS Credentials" value={config.aws_access_key} mono />}
            {config.collection_prefix && <InfoRow label="Collection Prefix" value={config.collection_prefix} mono />}
            {config.index_prefix && <InfoRow label="Index Prefix" value={config.index_prefix} mono />}
            <InfoRow label="Indexes" value={store.total_indexes} />
            <InfoRow label="Total Vectors" value={store.total_vectors >= 0 ? store.total_vectors.toLocaleString() : 'N/A'} />
        </Card>
    );
}

function IndexesTab({ provider }) {
    const [indexes, setIndexes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedIndex, setExpandedIndex] = useState(null);
    const [browseData, setBrowseData] = useState({});
    const [browseLoading, setBrowseLoading] = useState(false);

    useEffect(() => { loadIndexes(); }, [provider]);

    const loadIndexes = async () => {
        try {
            setLoading(true);
            const res = await adminApi.listVectorStoreIndexes(provider);
            setIndexes(res.indexes || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleBrowse = async (indexName, nextToken) => {
        try {
            setBrowseLoading(true);
            const res = await adminApi.browseVectors(provider, indexName, { limit: 20, nextToken });
            setBrowseData(prev => ({
                ...prev,
                [indexName]: {
                    vectors: nextToken
                        ? [...(prev[indexName]?.vectors || []), ...res.vectors]
                        : res.vectors,
                    next_token: res.next_token,
                    total: res.total,
                },
            }));
        } catch (err) {
            setError(err.message);
        } finally {
            setBrowseLoading(false);
        }
    };

    const toggleExpand = (indexName) => {
        if (expandedIndex === indexName) {
            setExpandedIndex(null);
            return;
        }
        setExpandedIndex(indexName);
        if (!browseData[indexName]) {
            handleBrowse(indexName);
        }
    };

    if (loading) return <Spinner size="md" text="Loading indexes..." center />;

    return (
        <div className="space-y-3">
            {error && <Alert variant="error" onClose={() => setError(null)}>{error}</Alert>}
            {indexes.length === 0 ? (
                <div className="text-center py-8 text-sm text-gray-500">No indexes found</div>
            ) : (
                indexes.map((idx) => (
                    <Card key={idx.name} className="hover:border-gray-300 transition-colors">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="flex items-center gap-2">
                                    <h4 className="text-sm font-medium text-gray-900 font-mono">{idx.name}</h4>
                                    {idx.collection_name && (
                                        <Badge variant="outline">{idx.collection_name}</Badge>
                                    )}
                                </div>
                                <div className="flex items-center gap-3 mt-1">
                                    <Badge variant={
                                        idx.status === 'green' || idx.status === 'active'
                                            ? 'success' : 'warning'
                                    }>{idx.status}</Badge>
                                    <span className="text-xs text-gray-400">
                                        {idx.vectors_count >= 0 ? idx.vectors_count.toLocaleString() : '?'} vectors
                                    </span>
                                    {idx.documents_count > 0 && (
                                        <span className="text-xs text-gray-400">
                                            {idx.documents_count} docs
                                        </span>
                                    )}
                                    {idx.total_content_size > 0 && (
                                        <span className="text-xs text-gray-400">
                                            {idx.total_content_size >= 1048576
                                                ? `${(idx.total_content_size / 1048576).toFixed(1)} MB`
                                                : idx.total_content_size >= 1024
                                                    ? `${(idx.total_content_size / 1024).toFixed(1)} KB`
                                                    : `${idx.total_content_size} B`
                                            }
                                        </span>
                                    )}
                                </div>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => toggleExpand(idx.name)}>
                                {expandedIndex === idx.name ? 'Collapse' : 'Browse'}
                            </Button>
                        </div>
                        {idx.config && Object.keys(idx.config).length > 0 && (
                            <div className="mt-2 pt-2 border-t border-gray-100">
                                {Object.entries(idx.config).map(([k, v]) => (
                                    v != null && <InfoRow key={k} label={k} value={typeof v === 'object' ? JSON.stringify(v) : String(v)} />
                                ))}
                            </div>
                        )}
                        {expandedIndex === idx.name && browseData[idx.name] && (
                            <div className="mt-3 pt-3 border-t border-gray-100">
                                <p className="text-xs text-gray-500 mb-2">
                                    Showing {browseData[idx.name].vectors.length} vectors
                                    {browseData[idx.name].total > 0 && ` of ${browseData[idx.name].total.toLocaleString()}`}
                                </p>
                                <div className="space-y-2 max-h-96 overflow-y-auto">
                                    {browseData[idx.name].vectors.map((vec) => (
                                        <VectorRow key={vec.id} vec={vec} />
                                    ))}
                                </div>
                                {browseData[idx.name].next_token && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        loading={browseLoading}
                                        onClick={() => handleBrowse(idx.name, browseData[idx.name].next_token)}
                                        className="mt-2"
                                    >
                                        Load More
                                    </Button>
                                )}
                            </div>
                        )}
                    </Card>
                ))
            )}
        </div>
    );
}

function VectorRow({ vec }) {
    return (
        <div className="bg-gray-50 rounded p-2.5 text-xs">
            <div className="flex items-center justify-between mb-1">
                <span className="font-mono text-gray-600 truncate max-w-xs">{vec.id}</span>
                {vec.score != null && (
                    <Badge variant={vec.score >= 0.7 ? 'success' : vec.score >= 0.4 ? 'warning' : 'default'}>
                        {(vec.score * 100).toFixed(1)}%
                    </Badge>
                )}
            </div>
            {vec.payload?.content && (
                <p className="text-gray-700 whitespace-pre-wrap line-clamp-3 mb-1">{vec.payload.content}</p>
            )}
            <div className="flex flex-wrap gap-1.5 mt-1">
                {Object.entries(vec.payload || {}).filter(([k]) => k !== 'content').map(([k, v]) => (
                    <span key={k} className="bg-white border border-gray-200 rounded px-1.5 py-0.5 text-[10px] text-gray-500">
                        {k}: {typeof v === 'object' ? JSON.stringify(v) : (typeof v === 'string' && v.length > 30 ? v.slice(0, 30) + '...' : String(v))}
                    </span>
                ))}
            </div>
        </div>
    );
}

function WorkbenchTab({ provider, indexes }) {
    const [selectedIndex, setSelectedIndex] = useState('');
    const [queryMode, setQueryMode] = useState('browse');
    const [vectorInput, setVectorInput] = useState('');
    const [topK, setTopK] = useState(10);
    const [filters, setFilters] = useState([]);
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [responseTime, setResponseTime] = useState(null);

    const handleExecute = async () => {
        if (!selectedIndex) return;
        setLoading(true);
        setError(null);
        setResponseTime(null);
        try {
            const start = performance.now();
            const activeFilters = filters.reduce((acc, f) => {
                if (f.key.trim() && f.value.trim()) acc[f.key.trim()] = f.value.trim();
                return acc;
            }, {});

            const payload = {
                top_k: topK,
                filters: Object.keys(activeFilters).length > 0 ? activeFilters : undefined,
            };

            if (queryMode === 'vector' && vectorInput.trim()) {
                try {
                    payload.query_vector = JSON.parse(vectorInput.trim());
                } catch {
                    setError('Invalid vector JSON. Enter a JSON array of numbers.');
                    setLoading(false);
                    return;
                }
            }

            const res = await adminApi.queryVectorStore(provider, selectedIndex, payload);
            setResponseTime(Math.round(performance.now() - start));
            setResults(res);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-4">
            {error && <Alert variant="error" onClose={() => setError(null)}>{error}</Alert>}
            <Card title="Query Configuration">
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Index</label>
                            <select
                                value={selectedIndex}
                                onChange={(e) => setSelectedIndex(e.target.value)}
                                className="w-full h-9 rounded-md border border-gray-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                            >
                                <option value="">Select an index...</option>
                                {indexes.map((idx) => (
                                    <option key={idx.name} value={idx.name}>{idx.name}</option>
                                ))}
                            </select>
                        </div>
                        <Input
                            label="Top K"
                            type="number"
                            value={topK}
                            onChange={(e) => setTopK(Math.max(1, Math.min(100, parseInt(e.target.value) || 10)))}
                        />
                    </div>
                    <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 text-sm">
                            <input
                                type="radio"
                                name="mode"
                                value="browse"
                                checked={queryMode === 'browse'}
                                onChange={() => setQueryMode('browse')}
                                className="accent-gray-900"
                            />
                            Browse (list vectors)
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                            <input
                                type="radio"
                                name="mode"
                                value="vector"
                                checked={queryMode === 'vector'}
                                onChange={() => setQueryMode('vector')}
                                className="accent-gray-900"
                            />
                            Vector Query (similarity search)
                        </label>
                    </div>
                    {queryMode === 'vector' && (
                        <Textarea
                            label="Query Vector (JSON array)"
                            value={vectorInput}
                            onChange={(e) => setVectorInput(e.target.value)}
                            placeholder="[0.1, 0.2, 0.3, ...]"
                            rows={3}
                            monospace
                        />
                    )}
                    <div>
                        <button
                            type="button"
                            onClick={() => {
                                if (filters.length === 0) setFilters([{ key: '', value: '' }]);
                                else setFilters([]);
                            }}
                            className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <svg className={`w-3 h-3 transition-transform ${filters.length > 0 ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            Metadata Filters
                        </button>
                        {filters.length > 0 && (
                            <div className="space-y-2 mt-2 border border-gray-100 rounded-md p-3 bg-gray-50/50">
                                {filters.map((f, idx) => (
                                    <div key={idx} className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            value={f.key}
                                            onChange={(e) => {
                                                const u = [...filters];
                                                u[idx] = { ...u[idx], key: e.target.value };
                                                setFilters(u);
                                            }}
                                            placeholder="key"
                                            className="h-8 flex-1 rounded border border-gray-200 px-2.5 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-1"
                                        />
                                        <span className="text-xs text-gray-300">=</span>
                                        <input
                                            type="text"
                                            value={f.value}
                                            onChange={(e) => {
                                                const u = [...filters];
                                                u[idx] = { ...u[idx], value: e.target.value };
                                                setFilters(u);
                                            }}
                                            placeholder="value"
                                            className="h-8 flex-1 rounded border border-gray-200 px-2.5 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-1"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setFilters(filters.filter((_, i) => i !== idx))}
                                            className="text-gray-300 hover:text-red-400 transition-colors p-1"
                                        >
                                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={() => setFilters([...filters, { key: '', value: '' }])}
                                    className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    + Add filter
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="flex justify-end">
                        <Button onClick={handleExecute} loading={loading} disabled={!selectedIndex}>
                            Execute Query
                        </Button>
                    </div>
                </div>
            </Card>

            {results && (
                <Card
                    title="Results"
                    headerRight={
                        <span className="text-xs text-gray-400">
                            {results.total} result{results.total !== 1 ? 's' : ''}
                            {responseTime != null && ` in ${responseTime}ms`}
                        </span>
                    }
                >
                    {results.vectors.length === 0 ? (
                        <div className="text-center py-6 text-sm text-gray-500">No vectors found</div>
                    ) : (
                        <div className="space-y-2">
                            {results.vectors.map((vec, idx) => (
                                <div key={vec.id} className="bg-gray-50 rounded-md p-3">
                                    <div className="flex items-center justify-between mb-1.5">
                                        <div className="flex items-center gap-2">
                                            <Badge variant="outline">#{idx + 1}</Badge>
                                            <span className="font-mono text-xs text-gray-600 truncate max-w-xs">{vec.id}</span>
                                        </div>
                                        {vec.score != null && (
                                            <Badge variant={vec.score >= 0.7 ? 'success' : vec.score >= 0.4 ? 'warning' : 'default'}>
                                                {(vec.score * 100).toFixed(1)}%
                                            </Badge>
                                        )}
                                    </div>
                                    {vec.payload?.content && (
                                        <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono leading-relaxed bg-white rounded p-2 mb-1.5 max-h-32 overflow-y-auto">
                                            {vec.payload.content}
                                        </pre>
                                    )}
                                    <div className="flex flex-wrap gap-1.5">
                                        {Object.entries(vec.payload || {}).filter(([k]) => k !== 'content').map(([k, v]) => (
                                            <span key={k} className="bg-white border border-gray-200 rounded px-1.5 py-0.5 text-[10px] text-gray-500">
                                                {k}: {typeof v === 'object' ? JSON.stringify(v) : (typeof v === 'string' && v.length > 40 ? v.slice(0, 40) + '...' : String(v))}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Card>
            )}
        </div>
    );
}

export default function AdminVectorStoreDetail() {
    const { provider } = useParams();
    const [store, setStore] = useState(null);
    const [indexes, setIndexes] = useState([]);
    const [activeTab, setActiveTab] = useState('workbench');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => { loadData(); }, [provider]);

    const loadData = async () => {
        try {
            setLoading(true);
            setError(null);
            const [storesRes, indexesRes] = await Promise.all([
                adminApi.listVectorStores(),
                adminApi.listVectorStoreIndexes(provider),
            ]);
            const found = (storesRes.stores || []).find(s => s.provider === provider);
            setStore(found || null);
            setIndexes(indexesRes.indexes || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <Spinner size="lg" text="Loading store details..." center />;

    const tabsWithCounts = TAB_LIST.map(t =>
        t.id === 'indexes' ? { ...t, count: indexes.length } : t
    );

    return (
        <div>
            <div className="flex items-center gap-3 mb-6">
                <Link
                    to="/admin/rag/vector-stores"
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                    </svg>
                </Link>
                <div>
                    <h1 className="text-lg font-semibold text-gray-900 capitalize">
                        {provider?.replace('_', ' ')}
                    </h1>
                    <p className="text-sm text-gray-500 mt-0.5">Vector store detail and query workbench</p>
                </div>
            </div>

            {error && <Alert variant="error" onClose={() => setError(null)} className="mb-4">{error}</Alert>}

            <Tabs tabs={tabsWithCounts} active={activeTab} onChange={setActiveTab} className="mb-4" />

            {activeTab === 'config' && <ConfigTab store={store} />}
            {activeTab === 'indexes' && <IndexesTab provider={provider} />}
            {activeTab === 'workbench' && <WorkbenchTab provider={provider} indexes={indexes} />}
        </div>
    );
}
